import React from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import Grid from '@material-ui/core/Grid';
import {format, isBefore} from 'date-fns';
import {useStore} from 'effector-react';
import i18next from 'i18next';
import {Control, Controller, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useHistory} from 'react-router-dom';
import * as yup from 'yup';

import {fetchParticipantList, fetchStorages} from 'api';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {DatePicker} from 'components/ui-kit/DatePicker';
import {AsyncSelect} from 'components/ui-kit/Select';

import {ROUTE} from 'core/routes';
import {profile$} from 'models/auth';
import {stepChanged, utilisationForm$, utilisationFormChanged} from 'models/documents/create';
import {onSetEditParticipant} from 'models/profile/common';
import {UtilisationFormData} from 'types';

import {FormContainer, FormField, FormWrapper, NavigationButtonsContainer} from '../common/styled';

import {DatePickerField} from './styled';

interface FormItemProps {
    control: Control;
    defaultValue?: string;
    name: string;
    setValue?: Function;
    fieldName?: string;
    getValues: Function;
    watch: Function;
}

export const StorageLocationId = ({
    control,
    setValue,
    getValues,
    watch,
    ...props
}: FormItemProps) => {
    const {t} = useTranslation();
    const utilisationForm = useStore(utilisationForm$);
    const productionLineId = watch('productionLineId');
    const profile = useStore(profile$);

    const NoStorageLocationSlot = () => {
        const history = useHistory();
        const [pending, setPending] = React.useState(false);
        const onRedirect = async () => {
            try {
                setPending(true);
                const participants = await fetchParticipantList();
                const user = participants.find((p) => p.id === profile?.client?.id);
                onSetEditParticipant(user || null);
                history.push(ROUTE.PROFILE_CREATE);
            } catch (error) {
                console.error(error);
            } finally {
                setPending(false);
            }
        };
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '10px',
                }}
            >
                Storage location is required
                <br />
                <Button
                    type="button"
                    variant={ButtonVariant.Outline}
                    onClick={onRedirect}
                    isLoading={pending}
                >
                    Go to profile and add it
                </Button>
            </div>
        );
    };

    const loadOptions = async () =>
        (await fetchStorages()).storageLocations.map((value) => ({
            value: value.id,
            label: value.storageName,
            meta: {name: value.storageName ?? ''},
        }));

    return (
        <Controller
            control={control}
            {...props}
            render={({value, onChange}, {invalid}) => (
                <AsyncSelect
                    key={productionLineId}
                    label={t('pages.documents.form.storageLocationName')}
                    isClearable
                    defaultOptions
                    noOptionsMessage={() => <NoStorageLocationSlot />}
                    value={{
                        value,
                        label: value
                            ? `${utilisationForm?.storageLocationName ||
                                  getValues(`storageLocationName`)}`
                            : value,
                    }}
                    onChange={(val: {value: string; meta?: {name: string}}) => {
                        val ? onChange(val.value) : onChange(null);
                        setValue?.(`storageLocationName`, val?.meta?.name);
                    }}
                    loadOptions={loadOptions}
                    hasError={invalid}
                />
            )}
        />
    );
};

const schema = yup.object().shape({
    // factoryId: yup.string().required(i18next.t('components.form.required')),
    // productionLineId: yup.string().required(i18next.t('components.form.required')),
    storageLocationId: yup.string().required(i18next.t('components.form.required')),
    manufacturingDate: yup
        .date()
        .nullable()
        .required('components.form.required'),
    applicationDate: yup
        .date()
        .nullable()
        .required('components.form.required'),
});

export const DocumentForm = () => {
    const {t} = useTranslation();
    const utilisationForm = useStore(utilisationForm$);
    const {control, handleSubmit, formState, getValues, setValue, watch} = useForm({
        mode: 'onChange',
        reValidateMode: 'onChange',
        resolver: yupResolver(schema),
        defaultValues: {
            ...utilisationForm,
            manufacturingDate: utilisationForm.manufacturingDate
                ? new Date(utilisationForm.manufacturingDate)
                : null,
            applicationDate: utilisationForm.applicationDate
                ? new Date(utilisationForm.applicationDate)
                : null,
        },
    });
    const onHandleChanged = (data: UtilisationFormData) => {
        const dateISOPattern = "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'";
        const dates = {
            applicationDate: format(data.applicationDate as Date, dateISOPattern),
            manufacturingDate: format(data.manufacturingDate as Date, dateISOPattern),
        };
        stepChanged(1);
        utilisationFormChanged({...data, ...dates});
    };
    return (
        <FormContainer>
            <form onSubmit={handleSubmit(onHandleChanged)}>
                <FormWrapper>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <DatePickerField>
                                    <Controller
                                        control={control}
                                        name="applicationDate"
                                        render={({onChange, value}) => (
                                            <DatePicker
                                                label={t(
                                                    'pages.documents.form.productApplicationLabel',
                                                )}
                                                maxDate={new Date()}
                                                value={value}
                                                onChange={(newDate: Date | null) => {
                                                    const {manufacturingDate} = control.getValues();
                                                    if (
                                                        newDate &&
                                                        manufacturingDate &&
                                                        typeof manufacturingDate !== 'string' &&
                                                        isBefore(newDate, manufacturingDate)
                                                    )
                                                        setValue('manufacturingDate', null);

                                                    onChange(newDate);
                                                }}
                                                hasError={!!formState.errors.applicationDate}
                                            />
                                        )}
                                    />
                                    {formState.errors.applicationDate && (
                                        <span style={{fontSize: 12, color: 'red'}}>
                                            {t(formState.errors.applicationDate.message as string)}
                                        </span>
                                    )}
                                </DatePickerField>
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <DatePickerField>
                                    <Controller
                                        control={control}
                                        name="manufacturingDate"
                                        render={({onChange, value}) => {
                                            const applicationDate = watch('applicationDate');
                                            return (
                                                <DatePicker
                                                    label={t(
                                                        'pages.documents.form.productManufacturingLabel',
                                                    )}
                                                    maxDate={
                                                        typeof applicationDate !== 'string'
                                                            ? applicationDate
                                                            : undefined
                                                    }
                                                    value={value}
                                                    onChange={onChange}
                                                    hasError={!!formState.errors.manufacturingDate}
                                                />
                                            );
                                        }}
                                    />
                                    {formState.errors.manufacturingDate && (
                                        <span style={{fontSize: 12, color: 'red'}}>
                                            {t(
                                                formState.errors.manufacturingDate
                                                    .message as string,
                                            )}
                                        </span>
                                    )}
                                </DatePickerField>
                            </FormField>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormField>
                                <StorageLocationId
                                    defaultValue={''}
                                    control={control}
                                    name={`storageLocationId`}
                                    setValue={setValue}
                                    getValues={getValues}
                                    watch={watch}
                                />
                                <div style={{display: 'none'}}>
                                    <Controller
                                        defaultValue={''}
                                        control={control}
                                        name="storageLocationName"
                                        render={(props) => <input value={props.value} readOnly />}
                                    />
                                </div>
                            </FormField>
                        </Grid>
                    </Grid>
                </FormWrapper>
                <NavigationButtonsContainer>
                    <span />
                    <Button
                        style={{width: 'auto'}}
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={!formState.isValid}
                    >
                        {t('components.form.nextStep')}
                    </Button>
                </NavigationButtonsContainer>
            </form>
        </FormContainer>
    );
};
